import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { Lock, Eye, EyeOff, Mail } from 'lucide-react';
import { useState, useEffect } from 'react';

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, login, error, clearError, isLoading } = useAuth();
  const location = useLocation();
  
  // Debug - Vérifier AuthGuard
  useEffect(() => {
    console.log('[AuthGuard]', { user: !!user, path: location.pathname, isLoading, error });
  }, [user, location.pathname, isLoading, error]);
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Test d'affichage d'urgence
  if (window.location.search.includes('debug=true')) {
    return (
      <div style={{ 
        background: 'lime', 
        color: 'black', 
        padding: '20px', 
        fontSize: '18px',
        minHeight: '100vh'
      }}>
        <h1>🚨 MODE DEBUG ACTIVÉ</h1>
        <p>User: {user ? 'Connecté' : 'Non connecté'}</p>
        <p>Loading: {isLoading ? 'Oui' : 'Non'}</p>
        <p>Error: {error || 'Aucune'}</p>
        <button onClick={() => window.location.href = window.location.pathname}>
          Retour normal
        </button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // Vérification email @memomundi.ai uniquement avec feedback
    if (!formData.email.endsWith('@memomundi.ai')) {
      setError('Accès restreint aux comptes administrateurs @memomundi.ai');
      return;
    }

    await login(formData.email, formData.password);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Vider le formulaire quand l'utilisateur n'est pas connecté
  useEffect(() => {
    if (!user) {
      setFormData({
        email: '',
        password: ''
      });
    }
  }, [user]);

  // Si l'utilisateur est connecté, afficher le contenu normal
  if (user) {
    return <>{children}</>;
  }

  // Sinon, afficher la page de garde style carte ID
  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        
        <div className="bg-white border border-stone-300 rounded-md p-8 relative overflow-hidden shadow-lg">
          {/* Filigrane logo en arrière-plan */}
          <div 
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 opacity-5 bg-center bg-no-repeat bg-contain"
            style={{
              backgroundImage: `url('https://memomundi.ai/pix/StampMMD.png')`
            }}
          />
          
          {/* Contenu de la carte */}
          <div className="relative z-10">
            {/* En-tête officiel */}
            <div className="text-center mb-8 border-b border-stone-200 pb-6">
              <span className="text-xs font-semibold text-steel-700 tracking-wide">MEMOMUNDI.AI</span>
              <h2 className="text-base font-serif font-bold text-steel-800 mt-1">
                ACCÈS ADMINISTRATEUR
              </h2>
              <p className="text-xs text-stone-600">
                Plateforme en développement - Accès restreint
              </p>
            </div>

            {/* Logo central */}
            <div className="text-center mb-6">
              <img 
                src="https://memomundi.ai/pix/StampMMD.png" 
                alt="MemoMundi.ai" 
                className="h-16 w-16 mx-auto mb-4 object-contain opacity-90"
                loading="lazy"
                onError={(e) => {
                  console.warn('[AuthGuard] Logo failed to load');
                  e.currentTarget.style.display = 'none';
                }}
              />
              
              <h1 className="text-2xl font-serif font-bold text-steel-800 mb-2">
                MemoMundi<span className="text-copper-600">.</span>ai
              </h1>
            </div>

            {/* Encadré ACCÈS RESTREINT */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Lock className="h-5 w-5 text-amber-600" />
                <span className="text-amber-800 font-semibold text-sm">ACCÈS RESTREINT - PHASE DE TEST</span>
              </div>
              <p className="text-amber-700 text-xs leading-relaxed text-center">
                Cette plateforme est actuellement en développement et réservée aux administrateurs autorisés. 
                Connexion obligatoire pour accéder au prototype.
              </p>
            </div>

            {/* Formulaire de connexion */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-steel-800 mb-2">
                  EMAIL ADMINISTRATEUR *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all font-mono"
                    placeholder="admin@memomundi.ai"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-steel-800 mb-2">
                  MOT DE PASSE *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-steel-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !formData.email.endsWith('@memomundi.ai')}
                className="w-full bg-steel-800 text-white px-8 py-4 rounded-md hover:bg-steel-900 transition-all duration-200 shadow-lg border-2 border-steel-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <div className="animate-pulse-soft">VÉRIFICATION...</div>
                ) : (
                  <>
                    <img 
                      src="https://memomundi.ai/pix/StampMMD.png" 
                      alt="MMD" 
                      className="h-5 w-5"
                     loading="lazy"
                     onError={(e) => e.currentTarget.style.display = 'none'}
                    />
                    <span>ACCÉDER AU PROTOTYPE</span>
                  </>
                )}
              </button>
            </form>

            {/* Restriction d'accès */}
            <div className="mt-6 p-3 bg-steel-50 border border-steel-200 rounded-md text-center">
              <p className="text-xs text-steel-700 font-medium">
                🔒 ACCÈS LIMITÉ AUX COMPTES @memomundi.ai
              </p>
              <p className="text-xs text-stone-600 mt-1">
                Seuls les administrateurs autorisés peuvent accéder au prototype
              </p>
            </div>
          </div>
        </div>

        {/* Informations légales */}
        <div className="text-center text-xs text-stone-600 mt-6 bg-white border border-stone-200 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <img 
              src="https://memomundi.ai/pix/StampMMD.png" 
              alt="MemoMundi.ai" 
              className="h-4 w-4"
             loading="lazy"
             onError={(e) => e.currentTarget.style.display = 'none'}
            />
            <span className="font-semibold">MEMOMUNDI.AI - PROTOTYPE SÉCURISÉ</span>
          </div>
          <p>
            Plateforme en développement - Accès administrateur uniquement
            <br />
            🇫🇷 Concept protégé INPI • 🇪🇺 Copyright.eu • 🌍 OMPI (en cours)
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthGuard;